import { isDefined } from '@sgme/fp';
import { Button, VStack } from '@sgme/ui';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import type { PredealCheck } from '@/types/common.ts';
import type { IntlMessageId } from '@/types/intl';

type ErrorRatesProps = {
  code?: string; // 'EM_OVERSHOT_LIMIT';
  message?: string; // 'The amount requested is too high. Please resubmit with a lower amount. user limit 80000 USD.';

  multipass?: {
    code?: string;
    sessionId?: string;
    predealChecks: PredealCheck[];
  };
};

export const ErrorRates = (props: ErrorRatesProps) => {
  const { code, message, multipass } = props;
  const {
    currentUser: { userType },
  } = useUserContext();
  const isInternalSales = userType === 'Internal Sales';
  const { formatMessage } = useIntl();

  const dispatch = useAppDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => setIsModalOpen(false);

  const sendHelpRequest = () => {
    dispatch(
      sendHelpRequestThunk({
        config: 'general',
        details: JSON.stringify({
          error: code,
          message,
          multipassSessionId: multipass?.sessionId,
          multipassErrorCode: multipass?.code,
          predealChecks: multipass?.predealChecks,
        }),
      }),
    );

    closeModal();
  };

  return (
    <>
      <VStack
        fullWidth
        horizontalAlignment="center"
        verticalAlignment="center"
        gap="8px"
        style={{ height: 100 }}
        className="bg-danger bg-opacity-10"
        data-e2e="rate-error"
      >
        <div className="text-danger text-center px-2" data-e2e="error-message">
          <strong>
            <FormattedMessage id="error" />
          </strong>
          {': '}
          {isDefined(code) ? (
            <FormattedMessage
              id={`error.${code}` as IntlMessageId}
              values={{ isInternalSales }}
              defaultMessage={formatMessage({ id: 'error.EM_GENERIC_ERROR' })}
            />
          ) : (
            (message ?? <FormattedMessage id="error.UNKNOWN" />)
          )}
        </div>

        {isDefined(multipass) && (
          <UsertypeGuard allow={['Internal Sales', 'Internal Trader']}>
            <Button purpose="primary" color="danger" size="sm" onClick={openModal} data-e2e="show-error-detail-btn">
              <FormattedMessage id="tile.error.viewSGLog" />
            </Button>
          </UsertypeGuard>
        )}
      </VStack>

      {isDefined(multipass) && (
        <Modal
          show={isModalOpen}
          onHide={closeModal}
          centered
          contentClassName="border-danger"
          size="lg"
          data-e2e="error-detail-modal"
        >
          <div className="border border-danger">
            <Modal.Header closeButton>
              <Modal.Title className="text-danger">
                <FormattedMessage id="tile.error.SGLogModal.tile" />
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="d-flex flex-column" style={{ maxWidth: 464 }}>
              <div className="row flex-nowrap mb-3">
                <aside className="col-4">
                  <FormattedMessage id="tile.error.SGLogModal.errorCode" />
                </aside>
                <div className="col-8 fw-bold" data-e2e="error-detail-code">
                  {code}
                </div>
              </div>

              <div className="row flex-nowrap mb-3">
                <aside className="col-4">
                  <FormattedMessage id="tile.error.SGLogModal.reasonType" />
                </aside>
                <div className="col-8 fw-bold" data-e2e="error-detail-message">
                  {message}
                </div>
              </div>

              <div className="row flex-nowrap mb-3">
                <aside className="col-4">
                  <FormattedMessage id="tile.error.SGLogModal.multipassSessionId" />
                </aside>
                <div className="col-8 fw-bold" data-e2e="error-detail-mp-session-id">
                  {multipass.sessionId}
                </div>
              </div>

              <div className="row flex-nowrap mb-3">
                <aside className="col-4">
                  <FormattedMessage id="tile.error.SGLogModal.multipassErrorCode" />
                </aside>
                <div className="col-8 fw-bold" data-e2e="error-detail-mp-code">
                  {multipass.code}
                </div>
              </div>

              {/*{isDefined(quoteId) && (*/}
              {/*  <div className="row flex-nowrap mb-3">*/}
              {/*    <aside className="col-4 fw-bold">*/}
              {/*      <FormattedMessage id="tile.error.SGLogModal.quoteId" />*/}
              {/*    </aside>*/}
              {/*    <div className="col-8">{quoteId}</div>*/}
              {/*  </div>*/}
              {/*)}*/}
            </Modal.Body>

            <Modal.Footer>
              <Button purpose="primary" onClick={sendHelpRequest} data-e2e="error-detail-send-help-request-btn">
                <FormattedMessage id="tile.error.sendHelpRequest" />
              </Button>

              <Button purpose="primary" color="secondary" onClick={closeModal} data-e2e="error-detail-close-btn">
                <FormattedMessage id="tile.error.close" />
              </Button>
            </Modal.Footer>
          </div>
        </Modal>
      )}
    </>
  );
};
